import type { Entity } from '@mathflat/domain/@entities/Lecture/dto'
import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain.ts'
import type { PlayerTime } from '@mathflat/videoplayer'
import { forEach, merge } from 'lodash-es'

import type { ProblemScoring } from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoring.trait'

import { cryptoUtils } from '../utils/cryptoUtils'
import { parseBooleanStringToBoolean } from '../utils/parsers'
import { PERSIST_KEYS_BASE } from './persistKeys'

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

type ScoringData<T extends 'WORKSHEET' | 'WORKBOOK' = 'WORKSHEET' | 'WORKBOOK'> = {
  scoringId: ProblemScoring<T>['id']
  localAnswer: ProblemScoring<T>['_localAutoScoredAnswer'] | ProblemScoring<T>['_localScoring']
}

interface StorageService {
  update(target: Omit<DeepPartial<this>, 'update'>): void
  clear(): void
}

type VideoDataKey = 'lecture' | 'assigned-lecture' | 'problem-solving-video'
type VideoDataValue = Pick<PlayerTime, 'current'>
// type VideoDataValue = Pick<PlayerConfig, 'resolutionInfo' | 'playbackRate'> & Pick<PlayerTime, 'current'>

export const USER_STORAGE_KEYS = {
  studentId: 'studentId',
  parentId: 'parentId',
  academyId: 'academyId',
  beforeLoginId: 'beforeLoginId',
  isInitialPassword: 'isInitialPassword',
  isPasswordChangedRequired: 'isPasswordChangedRequired',
} as const

export class LocalStorageService implements StorageService {
  user = {
    studentId: localStorage.getItem(USER_STORAGE_KEYS.studentId) as StudentDomain.Id | null,
    parentId: localStorage.getItem(USER_STORAGE_KEYS.parentId),
    beforeLoginId: localStorage.getItem(USER_STORAGE_KEYS.beforeLoginId),
    academyId: localStorage.getItem(USER_STORAGE_KEYS.academyId),
    isInitialPassword:
      localStorage.getItem(USER_STORAGE_KEYS.isInitialPassword) === null
        ? null
        : parseBooleanStringToBoolean(localStorage.getItem(USER_STORAGE_KEYS.isInitialPassword)!),
    isPasswordChangedRequired:
      localStorage.getItem(USER_STORAGE_KEYS.isPasswordChangedRequired) === null
        ? null
        : parseBooleanStringToBoolean(
            localStorage.getItem(USER_STORAGE_KEYS.isPasswordChangedRequired)!,
          ),
  } as const

  // 현재 기기에 한번이라도 로그인했던 유저Id Set([]) : 사용 가이드 유도 팝업을 위함
  userIdsOnDevice = localStorage.getItem('userIdsOnDevice')
    ? new Set(JSON.parse(localStorage.getItem('userIdsOnDevice')!))
    : new Set([])

  scoring = localStorage.getItem('scoring')
    ? new Map<
        ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'],
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localAutoScoredAnswer']
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localScoring']
      >(JSON.parse(localStorage.getItem('scoring')!))
    : new Map<
        ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'],
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localAutoScoredAnswer']
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localScoring']
      >()

  deviceId = localStorage.getItem('deviceId')

  // auth = {
  //   token: localStorage.getItem('token'),
  // } as const

  // userIdsOnDevice : 현재 기기에 처음으로 로그인 한 기기인지 확인

  isFirstUserOnDevice() {
    // 현재 사용자의 id와 스토리지의 사용자id 중 일치하는게 있는지 확인
    const base64UserId = cryptoUtils.encodeBase64(this.user.studentId as string)
    return !this.userIdsOnDevice.has(base64UserId)
  }
  // userIdsOnDevice : 동영상 유도팝업이 다시보여지지 않기 위해 storage에 현재사용자의 id를 set
  setUserIdsOnDevice() {
    const userIds = this.userIdsOnDevice.add(
      cryptoUtils.encodeBase64(this.user.studentId as string),
    )
    this.userIdsOnDevice = userIds
    localStorage.setItem('userIdsOnDevice', JSON.stringify([...userIds]))
  }
  private _videoData: Record<VideoDataKey, Map<Entity.Lecture['id'], VideoDataValue>> = {
    lecture: localStorage.getItem('VIDEO_INFO-lecture')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-lecture')!))
      : new Map(),
    'assigned-lecture': localStorage.getItem('VIDEO_INFO-assigned-lecture')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-assigned-lecture')!))
      : new Map(),
    'problem-solving-video': localStorage.getItem('VIDEO_INFO-problem-solving-video')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-problem-solving-video')!))
      : new Map(),
  }

  get videoData() {
    return this._videoData
  }

  setVideoData(key: VideoDataKey, videoId: Entity.Lecture['id'], value: VideoDataValue) {
    this._videoData[key].set(videoId, value)
    localStorage.setItem(`VIDEO_INFO-${key}`, JSON.stringify([...this._videoData[key].entries()]))
  }

  removeVideoData(key: VideoDataKey, videoId: Entity.Lecture['id']) {
    this._videoData[key].delete(videoId)
    localStorage.setItem(`VIDEO_INFO-${key}`, JSON.stringify([...this._videoData[key].entries()]))
  }

  updateInitialData = {
    qr: localStorage.getItem('qr') ?? 'FALSE',
  }

  device = {
    ver: localStorage.getItem('ver'),
  }

  learningProcess = {
    isVisitedConceptLearning: localStorage.getItem('isVisitedConceptLearning') || 'FALSE',
  }

  pushNotification = {
    deniedTimestamp: localStorage.getItem('pushNotificationDeniedTimestamp'),
  }

  cas = {
    skipSubmitConfirm: localStorage.getItem('casSkipSubmitConfirm') === 'true',
    skipBackConfirm: localStorage.getItem('casSkipBackConfirm') === 'true',
  }

  update(target: Omit<DeepPartial<this>, 'update'>) {
    merge(this, target)
    forEach(target, (value, key) => {
      forEach(value, (_value, _key) => {
        localStorage.setItem(_key, _value)
      })
    })
  }

  clear() {
    // 로컬스토리지를 clear해도 남아있어야하는 스토리지 keys.
    //   PERSIST_KEYS_BASE 는 인라인 explicit logout 과 공유 (단일 owner).
    //   INTEGRATION_KEY_V1_* 는 모두 logout 시 제거 (재마이그 필요 + dev override 도 reset OK).
    const PERSIST_KEYS = PERSIST_KEYS_BASE

    const persistItems = Object.entries(localStorage)
      .filter(([key]) => PERSIST_KEYS.some((epkKey) => key.includes(epkKey)))
      .map(([key, value]) => [key, value])

    localStorage.clear()
    persistItems.forEach(([key, value]) => {
      localStorage.setItem(key, value)
    })

    this.user = {
      studentId: null,
      parentId: null,
      beforeLoginId: null,
      academyId: null,
      isInitialPassword: null,
      isPasswordChangedRequired: null,
    }
    // this.auth = { token: null }
    this.device = { ver: null }
    this.scoring = new Map<
      ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'],
      | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localAutoScoredAnswer']
      | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localScoring']
    >()
    this.cas = { skipSubmitConfirm: false, skipBackConfirm: false }
  }

  clearIsInitialPassword() {
    localStorage.removeItem(USER_STORAGE_KEYS.isInitialPassword)
    this.user = {
      ...this.user,
      isInitialPassword: null,
    }
  }

  clearPasswordChangeRequired() {
    localStorage.removeItem(USER_STORAGE_KEYS.isPasswordChangedRequired)
    this.user = {
      ...this.user,
      isPasswordChangedRequired: null,
    }
  }

  // 학부모 세션 정리 — 선생님앱에서 학생앱 대리 로그인 전환 시 호출
  // parentId를 명시적으로 비우지 않으면 userType getter 가 계속 PARENT 로 판정된다.
  clearParentSession() {
    localStorage.removeItem(USER_STORAGE_KEYS.parentId)
    this.user = {
      ...this.user,
      parentId: null,
    }
  }

  setScoringData<T extends 'WORKSHEET' | 'WORKBOOK'>(params: ScoringData<T>) {
    this.scoring.set(params.scoringId, params.localAnswer)
    localStorage.setItem('scoring', JSON.stringify([...this.scoring.entries()]))
  }

  removeScoringData<T extends 'WORKSHEET' | 'WORKBOOK'>(params: Pick<ScoringData<T>, 'scoringId'>) {
    this.scoring.delete(params.scoringId)
    localStorage.setItem('scoring', JSON.stringify([...this.scoring.entries()]))
  }
}

// class SessionStorageService implements StorageService {
//   auth = {
//     token: sessionStorage.getItem('token'),
//   } as const

//   update(target: Omit<DeepPartial<this>, 'update'>) {
//     merge(this, target)
//     forEach(target, (value, key) => {
//       forEach(value, (_value, _key) => {
//         sessionStorage.setItem(_key, _value)
//       })
//     })
//   }

//   clear() {
//     sessionStorage.clear()
//     this.auth = { token: null }
//   }
// }

export const localStorageService = new LocalStorageService()
